import { Suspense, lazy, useEffect, useMemo } from 'react';

import { type BackdropPick, type LoginDesign, cursorOverlays } from '@/lib/login/loginDesign';
import { holdLightSeason } from '@/pages/docs/lab/design/lightSeason';

import { LOGIN_GRID_STYLES, LoginGridShow } from './LoginGridShow';
import { LOGIN_TRAIL_STYLES, LoginTrail } from './LoginTrail';
import { LOGIN_WAVES_STYLES, LoginWaves } from './LoginWaves';

/**
 * 로그인 화면의 바탕 — 시안들을 이 한 곳에서 그린다(사용자 지시 2026-09-15).
 *
 * <h2>무엇이 서는가는 밖에서 정한다</h2>
 * 바탕 시안은 다섯이다 — 사진 둘(<b>무작위 격자</b> · <b>수평 시차</b>)과 장면 셋(<b>히어로</b> ·
 * <b>단지</b> · <b>수영장</b>). 다섯은 모두 같은 자리를 다투므로 하나만 서고, 켜 둔 것이 둘 이상이면
 * <b>들어올 때마다 무작위로</b> 하나가 선다 — 「여럿 켬」 이 곧 「번갈아」다(사용자 결정 2026-10-08).
 *
 * <p>뽑기는 이 조각이 아니라 {@code useBackdropPick} 이 하고, 결과({@code pick})를 받아 그리기만 한다.
 * 장면이 서면 카드가 오른쪽으로 물러나야 하는데, 카드는 이 조각 밖에 있다 — 둘이 따로 뽑으면 단지가
 * 깔렸는데 카드는 가운데에 서는 일이 생긴다.
 *
 * <h2>커서 효과는 사진 위에만 겹친다</h2>
 * 커서 효과(꼬리 · 물결)는 사진을 쓰지 않으므로 사진 시안 위에 겹친다. 이쪽은 <b>고른다</b>
 * ({@code cursorStyle} · 사용자 지시 2026-09-15) — 둘 다 커서에 붙는 효과라 같이 그리면 화면에 커서가
 * 둘인 것처럼 보인다. 같은 이유로 <b>장면 위에는 겹치지 않는다</b> — 히어로는 커서를 따라 유체를 밀고,
 * 단지는 커서 쪽이 솟고, 수영장은 물이 커서를 따라온다.
 *
 * <h2>사진이 없으면 사진 시안은 서지 않는다</h2>
 * 켜 두었더라도 등록된 사진이 한 장도 없으면 뽑기 후보에서 빠진다 — 그때는 부모가 깔아 둔 바탕이
 * 그대로 보인다. 「사진을 올리세요」 같은 딱지는 걸지 않는다: 그 말을 들어야 하는 사람은 로그인하는
 * 직원이 아니라 관리자이고, 그 말은 설정 화면에 있다.
 */

/** three 는 크고, 로그인은 앱에서 가장 먼저 뜨는 화면이다 — 실제로 그릴 때만 받아온다. */
const LoginParallax = lazy(() => import('./LoginParallax').then((m) => ({ default: m.LoginParallax })));
/** 히어로는 three 에 셰이더 여러 판까지 얹는다 — 더더욱 그릴 때만 받아온다. */
const LoginHeroScene = lazy(() => import('./LoginHeroScene').then((m) => ({ default: m.LoginHeroScene })));
/** 단지·수영장도 three 를 싣는다. */
const LoginAptScene = lazy(() => import('./LoginAptScene').then((m) => ({ default: m.LoginAptScene })));
/**
 * 야경 두 판(사용자 지시 2026-10-21) — 단지도 켜 두었으면 흐름판(단지의 하루 → 저녁에 조명 둘 → 다시 단지), 야경만 켰으면
 * 아침 없이 조명만 도는 판.
 */
const LoginLightsScene = lazy(() => import('./LoginLightsScene').then((m) => ({ default: m.LoginLightsScene })));
const LoginLightsLoop = lazy(() => import('./LoginLightsLoop').then((m) => ({ default: m.LoginLightsLoop })));
const LoginSeaScene = lazy(() => import('./LoginSeaScene').then((m) => ({ default: m.LoginSeaScene })));
/** 열대 수족관(2026-10-28) — 13종의 텍스처를 그 자리에서 굽는다. 역시 그릴 때만. */
const LoginTankScene = lazy(() => import('./LoginTankScene').then((m) => ({ default: m.LoginTankScene })));
const LoginPoolScene = lazy(() => import('./LoginPoolScene').then((m) => ({ default: m.LoginPoolScene })));
/** 거리 걷기(2026-10-29) — 얼굴을 흐려 구운 실제 영상. 실험실 칸 그대로. */
const StreetWalk = lazy(() => import('@/pages/docs/lab/design/StreetWalk').then((m) => ({ default: m.StreetWalk })));
/** 세계 지도는 three 를 쓰지 않지만 대륙 자료(약 450KB)를 받는다 — 역시 그릴 때만. */
const LoginWorldScene = lazy(() => import('./LoginWorldScene').then((m) => ({ default: m.LoginWorldScene })));

/**
 * 수평 시차 칸의 규칙. <b>그 조각이 아니라 여기</b>에 둔다 — 저쪽은 three 를 싣고 있어 `lazy` 로만
 * 받아오는데, 규칙을 거기서 가져오면 그것을 읽으려고 모듈 전체가 첫 묶음에 딸려 온다.
 */
const LOGIN_PARALLAX_STYLES = `
.lpx{position:absolute;inset:0;overflow:hidden;background:#000;pointer-events:none}
.lpx canvas{position:absolute;inset:0;width:100%;height:100%;display:block}
`;

export function LoginBackdrop({ design, pick }: { design: LoginDesign; pick: BackdropPick }) {
  /* 색상환을 굴리는 것은 <b>한 벌에 한 번</b>이다. 렌더마다 뽑으면 아이디를 한 글자 칠 때마다
     구체의 색이 바뀐다 — 시안을 고르는 자리와 같은 이유로 붙들어 둔다. */
  const hueShift = useMemo(() => (design.hero.random ? Math.random() * 360 : 0), [design.hero.random]);
  const cursor = cursorOverlays(design, pick);
  /* 야경 시즌 — 연출이 바퀴를 열 때 읽는 스위치에 적는다(`lightSeason`). 장면 prop 으로 내리지 않는다: 그 길(LoginLightsScene ·
     BatchedField)은 앱 로그인 작업과 같이 쓰는 파일이다(2026-10-21). 시즌을 바꾸면 장면을 다시 세워 첫 바퀴부터 그 시즌으로. */
  const season = design.lightsSeason ?? null;
  useEffect(() => holdLightSeason(season), [season]);

  if (!pick && !cursor) return null;

  return (
    <>
      <style>{`${LOGIN_GRID_STYLES}${LOGIN_PARALLAX_STYLES}${LOGIN_TRAIL_STYLES}${LOGIN_WAVES_STYLES}`}</style>
      {pick === 'hero' && (
        <Suspense fallback={null}>
          <LoginHeroScene hero={design.hero} hueShift={hueShift} />
        </Suspense>
      )}
      {pick === 'apt' && (
        <Suspense fallback={null}>
          {/* 밤/낮은 처음 값으로만 읽힌다 — 바꾸면 다시 세운다. */}
          <LoginAptScene key={String(design.apt.night)} apt={design.apt} />
        </Suspense>
      )}
      {pick === 'lights' && (
        <Suspense fallback={null}>
          {design.aptEnabled ? (
            <LoginLightsScene key={`flow:${season ?? 'none'}`} />
          ) : (
            <LoginLightsLoop key={`loop:${season ?? 'none'}`} />
          )}
        </Suspense>
      )}
      {pick === 'sea' && (
        <Suspense fallback={null}>
          {/* 마릿수는 장면이 설 때 한 번 뽑는다 — 설정 미리보기에서 값을 바꾸면 다시 세운다. */}
          <LoginSeaScene key={JSON.stringify(design.sea ?? {})} sea={design.sea} />
        </Suspense>
      )}
      {pick === 'tank' && (
        <Suspense fallback={null}>
          <LoginTankScene />
        </Suspense>
      )}
      {pick === 'pool' && (
        <Suspense fallback={null}>
          <LoginPoolScene />
        </Suspense>
      )}
      {pick === 'world' && (
        <Suspense fallback={null}>
          <LoginWorldScene />
        </Suspense>
      )}
      {pick === 'walk' && (
        <Suspense fallback={null}>
          <StreetWalk chrome={false} />
        </Suspense>
      )}
      {pick === 'grid' && <LoginGridShow images={design.images} options={design.grid} />}
      {pick === 'parallax' && (
        <Suspense fallback={null}>
          <LoginParallax images={design.images} options={design.parallax} />
        </Suspense>
      )}
      {/* 커서 효과는 둘 중 하나다. 같이 그리면 화면에 커서가 둘인 것처럼 보인다.
          `solid` — 아래에 사진 시안이 없을 때만 원본의 어두운 바탕을 깐다. */}
      {cursor &&
        (design.cursorStyle === 'trail' ? (
          <LoginTrail options={design.trail} solid={pick === null} />
        ) : (
          <LoginWaves options={design.waves} solid={pick === null} />
        ))}
    </>
  );
}
